import { forwardRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  MdOutlineSubscriptions,
  MdSystemUpdateAlt,
  MdCampaign,
} from "react-icons/md";
import { FaBlog, FaRocket } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import { LuUsers } from "react-icons/lu";
import { MdOutlineDashboard } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";
import { HiUsers } from "react-icons/hi2";

const Sidebar = forwardRef(({}, ref) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get user role from localStorage
  const userRole = localStorage.getItem("adminUserRole") || "Admin";

  // Define which tabs each role can see
  const canSeeTab = (tabName) => {
    if (userRole === "Admin") {
      return true; // Admin can see all tabs
    }
    if (userRole === "Author") {
      return ["Dashboard", "Notifications", "Blog"].includes(tabName);
    }
    if (userRole === "CSR") {
      return [
        "Dashboard",
        "Users",
        "Subscription",
        "Match User",
        "User Profiles",
      ].includes(tabName);
    }
    return false;
  };

  return (
    <div
      ref={ref}
      className="lg:ml-8 px-2 ml-2 lg:px-0 w-full md:w-auto lg:w-80 h-full min-h-[calc(100vh-120px)] bg-black rounded-xl shadow-xl
              overflow-auto no-scrollbar text-black">
      <div className="flex flex-col mt-6 md:mt-10 gap-1 md:gap-0 justify-start">
        {canSeeTab("Dashboard") && (
          <div
            onClick={() => {
              if (location.pathname !== "/") {
                navigate("/dashboard");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                            cursor-pointer mb-1 flex items-center gap-3
                            ${
                              location.pathname == "/"
                                ? "bg-white text-black rounded-full"
                                : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                            }`}>
              <div className="md:mr-3">
                <MdOutlineDashboard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Dashboard</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-1 gap-1">
        {canSeeTab("Users") && (
          <div
            onClick={() => {
              if (location.pathname !== "/topusers") {
                navigate("/topusers");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "topusers"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <CiUser className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Users</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Subscription") && (
          <div
            onClick={() => {
              if (location.pathname !== "/subscription") {
                navigate("/subscription");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "subscription"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <MdOutlineSubscriptions className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Subscription</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Notifications") && (
          <div
            onClick={() => {
              if (location.pathname !== "/notifications") {
                navigate("/notifications");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "notifications"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <IoMdNotificationsOutline className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Notifications</p>
              </div>
            </div>
          </div>
        )}
        {canSeeTab("Promotional Messages") && (
          <div
            onClick={() => {
              if (location.pathname !== "/promotional-messages") {
                navigate("/promotional-messages");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "promotional-messages"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <MdCampaign className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Promotional Messages</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Blog") && (
          <div
            onClick={() => {
              if (location.pathname !== "/blogs") {
                navigate("/blogs");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes("blogs")
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <FaBlog className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Blog</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("User Profiles") && (
          <div
            onClick={() => {
              if (location.pathname !== "/user-profiles") {
                navigate("/user-profiles");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/user-profiles"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <LuUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">User Profiles</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Match User") && (
          <div
            onClick={() => {
              if (location.pathname !== "/match-user") {
                navigate("/match-user");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/match-user"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <FaUserFriends className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Match User</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("App Version Control") && (
          <div
            onClick={() => {
              if (location.pathname !== "/app-version") {
                navigate("/app-version");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/app-version"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <MdSystemUpdateAlt className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">App Version Control</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Admin Panel Users") && (
          <div
            onClick={() => {
              if (location.pathname !== "/admin-panel-users") {
                navigate("/admin-panel-users");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/admin-panel-users"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <HiUsers className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Admin Panel Users</p>
              </div>
            </div>
          </div>
        )}

        {canSeeTab("Boost History") && (
          <div
            onClick={() => {
              if (location.pathname !== "/boost-history") {
                navigate("/boost-history");
              }
            }}>
            <div
              className={`w-full lg:px-5 px-4 py-3 lg:mx-5 text-center
                                     cursor-pointer mb-1 flex items-center gap-3
                                         ${
                                           location.pathname.includes(
                                             "/boost-history"
                                           )
                                             ? "bg-white text-black rounded-full"
                                             : "bg-transparent text-white font-[600] hover:text-white hover:bg-white hover:bg-opacity-20 hover:rounded-full"
                                         }`}>
              <div className="md:mr-3">
                <FaRocket className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">Boost History</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Sidebar;
